import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import { useTranslation } from 'react-i18next';

import AssistantRagStorageService from '@shared/services/AssistantRagStorageService';
import { formatSpotRegion } from '@shared/utils/assistantScript';
import type {
  IAssistantRagVectorStorage,
  ICreateAssistantRagVectorStorage,
  IUpdateAssistantRagVectorStorage,
} from '@shared/interfaces/assistantRagStorage';

import AssistantContextDialogWrapper from '@core/assistant/Screens/DefaultScreen/AssistantContextDialogWrapper';

import type { AiEntity } from './types';
import { useAiInspectorStyles } from './inspectorStyles';

interface IKnowledgeTabProps {
  entity: AiEntity;
  brandingId: string | undefined;
  mapName: string | undefined;
  onChanged: () => Promise<void>;
}

const formatSize = (bytes: number | undefined): string => {
  if (!bytes || Number.isNaN(bytes)) {
    return '';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const KnowledgeTab = ({
  entity,
  brandingId,
  mapName,
  onChanged,
}: IKnowledgeTabProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] =
    useState<IAssistantRagVectorStorage | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<IAssistantRagVectorStorage | null>(null);
  const [busyDelete, setBusyDelete] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const handleCreate = useCallback(
    async (
      values:
        | ICreateAssistantRagVectorStorage
        | IUpdateAssistantRagVectorStorage,
    ) => {
      const payload: ICreateAssistantRagVectorStorage = {
        ...(values as ICreateAssistantRagVectorStorage),
        ...(brandingId ? { brandingId } : {}),
      };
      await AssistantRagStorageService.createAssistantRagStorageItem(payload);
      setAddOpen(false);
      await onChanged();
    },
    [brandingId, onChanged],
  );

  const handleUpdate = useCallback(
    async (
      values:
        | ICreateAssistantRagVectorStorage
        | IUpdateAssistantRagVectorStorage,
    ) => {
      if (!editTarget) {
        return;
      }
      await AssistantRagStorageService.updateAssistantRagStorageItem(
        editTarget.ragVectorLinkId,
        values as IUpdateAssistantRagVectorStorage,
      );
      setEditTarget(null);
      await onChanged();
    },
    [editTarget, onChanged],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }
    setBusyDelete(true);
    try {
      await AssistantRagStorageService.deleteAssistantRagStorageItem(
        deleteTarget.ragVectorLinkId,
      );
      setDeleteTarget(null);
      await onChanged();
    } catch (deleteError) {
      console.error(
        `Failed to delete RAG item: ${(deleteError as Error).message}`,
        { ragVectorLinkId: deleteTarget.ragVectorLinkId },
      );
    } finally {
      setBusyDelete(false);
    }
  }, [deleteTarget, onChanged]);

  // Retry triggers a PATCH that keeps the item's data — backend re-runs
  // indexing for the new payload. We just patch with the same tags as a
  // minimal no-op-ish change and let the polling pick up the new status.
  const handleRetry = useCallback(
    async (item: IAssistantRagVectorStorage) => {
      setRetryingId(item.ragVectorLinkId);
      try {
        const update: IUpdateAssistantRagVectorStorage = {
          tags: item.tags,
          ...(item.title ? { title: item.title } : {}),
        };
        await AssistantRagStorageService.updateAssistantRagStorageItem(
          item.ragVectorLinkId,
          update,
        );
        await onChanged();
      } catch (retryError) {
        console.error(
          `Failed to retry RAG indexing: ${(retryError as Error).message}`,
          { ragVectorLinkId: item.ragVectorLinkId },
        );
      } finally {
        setRetryingId(null);
      }
    },
    [onChanged],
  );

  return (
    <Box>
      <Box sx={classes.card}>
        <Box sx={classes.cardHeader}>
          <Typography component="span" sx={classes.cardHeaderTitle}>
            <LibraryBooksOutlinedIcon sx={{ fontSize: 13 }} />
            {translate('editorMode.aiKnowledge')}
            {entity.contextItems.length > 0
              ? ` (${entity.contextItems.length})`
              : ''}
            <Tooltip
              title={translate('editorMode.aiKnowledgeCountHint')}
              placement="top"
              arrow
              componentsProps={{
                tooltip: { sx: { fontSize: '12px' } },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15, opacity: 0.7 }} />
            </Tooltip>
          </Typography>
        </Box>

        {entity.contextItems.length === 0 ? (
          <Typography sx={classes.footHint}>
            {translate('editorMode.aiNoKnowledgeHint')}
          </Typography>
        ) : (
          entity.contextItems.map((item, index) => {
            const kind = item.parsedDocumentId ? 'file' : 'text';
            const sizeLabel = formatSize(item.sizeInBytes);
            return (
              <React.Fragment key={item.ragVectorLinkId}>
                <Box
                  sx={[
                    classes.knowledgeRow,
                    index > 0 && classes.knowledgeRowDivider,
                  ]}
                >
                  {kind === 'file' ? (
                    <InsertDriveFileOutlinedIcon sx={classes.knowledgeIcon} />
                  ) : (
                    <TextSnippetOutlinedIcon sx={classes.knowledgeIcon} />
                  )}
                  <Box sx={classes.knowledgeText}>
                    <Typography sx={classes.knowledgeTitle}>
                      {item.title?.trim() ||
                        item.sourceFileName ||
                        translate('editorMode.aiUntitled')}
                    </Typography>
                    <Typography sx={classes.knowledgeMeta}>
                      {kind === 'file'
                        ? translate('editorMode.aiKnowledgeKindFile')
                        : translate('editorMode.aiKnowledgeKindText')}
                      {sizeLabel ? ` · ${sizeLabel}` : ''}
                    </Typography>
                  </Box>
                  {item.status === 'READY' ? (
                    <Box
                      component="span"
                      sx={[classes.knowledgeBadge, classes.knowledgeBadgeReady]}
                    >
                      <CheckIcon />
                      {translate('editorMode.aiKnowledgeReady')}
                    </Box>
                  ) : item.status === 'INDEXING_FAILED' ? (
                    <Box
                      component="span"
                      sx={[
                        classes.knowledgeBadge,
                        classes.knowledgeBadgeFailed,
                      ]}
                    >
                      <ErrorOutlineOutlinedIcon />
                      {translate('editorMode.aiKnowledgeFailed')}
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={[
                        classes.knowledgeBadge,
                        classes.knowledgeBadgePending,
                      ]}
                    >
                      <CircularProgress size={11} thickness={6} />
                      {translate('editorMode.aiKnowledgeIndexing')}
                    </Box>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setEditTarget(item)}
                    sx={[classes.iconButton, { width: 28, height: 28 }]}
                    aria-label={translate('editorMode.aiEditKnowledgeItem', 'Edit item')}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget(item)}
                    sx={[
                      classes.iconButton,
                      classes.iconButtonDanger,
                      { width: 28, height: 28 },
                    ]}
                    aria-label={translate('editorMode.aiDeleteKnowledgeItem', 'Delete item')}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                {item.status === 'INDEXING_FAILED' ? (
                  <Box sx={classes.knowledgeError}>
                    <ErrorOutlineOutlinedIcon sx={{ fontSize: 11 }} />
                    <Tooltip
                      title={
                        item.indexingError?.code
                          ? `${item.indexingError.code}`
                          : ''
                      }
                      placement="top"
                    >
                      <Box component="span">
                        {item.indexingError?.message ||
                          translate('editorMode.aiKnowledgeIndexError')}
                      </Box>
                    </Tooltip>
                    <Box
                      component="button"
                      type="button"
                      sx={classes.linkButton}
                      onClick={() => handleRetry(item)}
                      disabled={retryingId === item.ragVectorLinkId}
                    >
                      {retryingId === item.ragVectorLinkId ? (
                        <RefreshIcon sx={{ fontSize: 11 }} />
                      ) : (
                        translate('editorMode.aiRetry')
                      )}
                    </Box>
                  </Box>
                ) : null}
              </React.Fragment>
            );
          })
        )}

        {entity.contextItems.length === 0 && (
          <Box
            component="button"
            type="button"
            sx={classes.knowledgeAddButton}
            onClick={() => setAddOpen(true)}
          >
            <AddIcon sx={{ fontSize: 13 }} />
            {translate('editorMode.aiAddKnowledge', 'Add knowledge — text or file')}
          </Box>
        )}
      </Box>

      {(addOpen || !!editTarget) && (
        <AssistantContextDialogWrapper
          open
          assistantRagVectorStorage={editTarget}
          tag={entity.primaryTrigger}
          title={formatSpotRegion(entity.primaryTrigger) || entity.name}
          mapName={mapName}
          onClose={() => {
            setAddOpen(false);
            setEditTarget(null);
          }}
          onSave={editTarget ? handleUpdate : handleCreate}
        />
      )}

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            background:
              'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
            borderRadius: '16px',
            color: '#e2e4ed',
            '& .MuiDialogTitle-root': { color: '#e2e4ed' },
            '& .MuiDialogContentText-root': { color: '#e2e4ed' },
            '& .MuiButton-text': { color: '#e2e4ed' },
            '& .MuiButton-textError': { color: '#f06d6d' },
          },
        }}
      >
        <DialogTitle>{translate('editorMode.aiDeleteKnowledge')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('editorMode.aiDeleteKnowledgeConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={busyDelete}>
            {translate('common.cancel')}
          </Button>
          <Button color="error" onClick={handleDelete} disabled={busyDelete}>
            {translate('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeTab;
